export default function WesleyChapelDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Wesley Chapel requests are family or child passport related — spring break and summer travel",
    },
    {
      value: "3–4 weeks",
      label: "average notice for youth sports tournament qualification and family vacation bookings",
    },
    {
      value: "Feb–Apr",
      label: "peak spring break season — passport renewals spike before Tampa cruise and resort departures",
    },
    {
      value: "48-hour",
      label: "shortest tournament departure window we've accommodated for a Wesley Chapel youth hockey team",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Wesley Chapel families — whether by cruise from Port Tampa Bay or by direct flight from TPA to Nassau. Families from Wiregrass Ranch and the surrounding subdivisions regularly sail to the Bahamas, Jamaica, the Cayman Islands, and Cozumel. All cruise passengers and most air travelers need valid passports, including children.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Canada is the most frequent international destination for Wesley Chapel youth sports teams. Hockey tournaments in Toronto, Montreal, and Vancouver; gymnastics competitions in Calgary and Ottawa; and soccer tournaments across Ontario draw young athletes from the Tampa Bay Skating Academy and Wiregrass Sports Campus. Canada requires valid passports for all travelers, including children, and has strict documentation requirements for minors traveling without both parents.",
      urgency: "Tournament qualification notice is typically 3–4 weeks — insufficient for standard processing if a passport has expired",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, and Los Cabos are staple destinations for Wesley Chapel families seeking beach vacations within a short flight. TPA offers connecting service through Atlanta, Houston, and Charlotte. Mexico requires six months of passport validity for entry, and airlines enforce this rule at check-in. All-inclusive resorts in Cancun and Playa del Carmen are particularly popular with Wesley Chapel's young-family demographic during spring break.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Wesley Chapel families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, and Dublin draw Wesley Chapel families for summer cultural vacations and educational travel. British Airways operates direct service from TPA to London Gatwick seasonally, and connections through Atlanta, Charlotte, and Philadelphia extend reach to the UK and Ireland. The UK requires the full duration of your stay in passport validity, and Ireland requires three months beyond your departure date.",
      urgency: "Summer vacation bookings happen in March–April — check passports before you book, not after",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Germany & Western Europe",
      context: "Germany, France, Switzerland, and the Netherlands attract Wesley Chapel medical professionals attending conferences and families on cultural vacations. The Schengen Area requires three months of passport validity beyond your planned departure. Frankfurt is accessible via Lufthansa's seasonal service from TPA, and connections through Atlanta and Charlotte serve Paris, Amsterdam, and other European hubs.",
      urgency: "Conference invitations arrive 4–6 weeks before the event — tight for standard processing if a passport needs renewal",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica & Latin America",
      context: "Costa Rica has become increasingly popular with Wesley Chapel families seeking eco-adventure vacations that blend beaches, rainforests, and wildlife. TPA connects through Atlanta, Houston, and Miami to San José and Liberia. Costa Rica requires six months of passport validity. Other Latin American destinations from Wesley Chapel include Panama, Belize, and Colombia — all with similar validity requirements.",
      urgency: "Eco-tourism bookings often happen 2–3 months out — check passport validity at booking to avoid last-minute stress",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Wesley Chapel Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Wesley Chapel
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Wesley Chapel travelers head to the Caribbean for family cruises, Canada for youth sports competitions, Europe for cultural vacations and medical conferences, and Latin America for eco-adventures. These are the destinations that create the most passport urgency for our Wesley Chapel clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Tampa Bay and Central Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Tampa", href: "/cities/tampa" },
              { name: "St. Petersburg", href: "/cities/st-petersburg" },
              { name: "Clearwater", href: "/cities/clearwater" },
              { name: "Lakewood Ranch", href: "/cities/lakewood-ranch" },
              { name: "Orlando", href: "/cities/orlando" },
              { name: "Sarasota", href: "/cities/sarasota" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}